import type { ReactNode, SVGProps } from "react";

/**
 * Sidebar glyphs, exported from the GMS Figma (node 277:3764) and inlined so
 * they take `currentColor` — the design's white glyphs recolour to brand blue
 * on the active pill, which a static `<img src=*.svg>` cannot do.
 *
 * Geometry is preserved from the export: every module icon is a 24×24 box;
 * the avatar is 49×49. Sizes are set by the caller in rem — these components
 * only fix the aspect ratio.
 */
type IconProps = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;

const base: IconProps = { fill: "none", "aria-hidden": true, focusable: false };

export function PeopleIcon(props: IconProps) {
	return (
		<svg viewBox="0 0 24 24" {...base} {...props}>
			<path
				fill="currentColor"
				d="M15.7499 3.5C18.1878 3.5 20.0232 5.4205 19.8407 7.94727C19.6752 10.2411 17.942 12.25 15.7499 12.25C13.5587 12.2499 11.8217 10.242 11.6591 7.94531C11.5708 6.69867 11.96 5.57488 12.7245 4.75977C13.4865 3.94731 14.5662 3.50003 15.7499 3.5ZM15.7499 5.5C15.0761 5.50003 14.5391 5.74883 14.1835 6.12793C13.8304 6.50441 13.602 7.0682 13.6542 7.80469C13.7617 9.32142 14.8569 10.2499 15.7499 10.25C16.642 10.25 17.7371 9.32151 17.8466 7.80273C17.9452 6.43526 17.0272 5.5 15.7499 5.5Z"
			/>
			<path
				fill="currentColor"
				d="M15.7504 13.25C19.0591 13.2501 22.5621 14.8607 23.4506 18.4834L23.4818 18.6504C23.5955 19.4983 23.0469 20.4998 21.9301 20.5H9.57068C8.38314 20.4997 7.831 19.3636 8.05115 18.4805L8.14197 18.1523C9.15727 14.8122 12.536 13.25 15.7504 13.25ZM15.7504 15.25C13.1483 15.25 10.902 16.4405 10.1361 18.5H21.3676C20.6063 16.4086 18.3657 15.2501 15.7504 15.25Z"
			/>
			<path
				fill="currentColor"
				d="M6.89075 5C8.95836 5.00002 10.5257 6.65715 10.3722 8.78809C10.2349 10.6923 8.77757 12.4375 6.89075 12.4375C5.00482 12.4375 3.5443 10.6932 3.4093 8.78613C3.25631 6.62071 4.82979 5 6.89075 5ZM6.89075 7C5.98368 7 5.33255 7.64328 5.40344 8.64551C5.48413 9.7843 6.31296 10.4375 6.89075 10.4375C7.46748 10.4375 8.2957 9.78495 8.37805 8.64453C8.44857 7.66581 7.79122 7.00002 6.89075 7Z"
			/>
			<path
				fill="currentColor"
				d="M6.89026 12.8071C7.97571 12.8071 9.05991 12.9709 10.0729 13.435C10.5749 13.6651 10.7951 14.2581 10.5651 14.7602C10.335 15.2622 9.7419 15.4832 9.23987 15.2534C8.56065 14.9422 7.78012 14.8071 6.89026 14.8071C4.98635 14.8072 3.37473 15.6234 2.71057 17.0004H7.21838C7.77067 17.0004 8.21838 17.4481 8.21838 18.0004C8.21817 18.5525 7.77054 19.0004 7.21838 19.0004H1.95569C0.8489 19.0004 0.346482 17.9416 0.545532 17.1381L0.546509 17.1371C1.2871 14.166 4.19008 12.8072 6.89026 12.8071Z"
			/>
		</svg>
	);
}

export function UserCircleIcon(props: IconProps) {
	return (
		<svg viewBox="0 0 49 49" {...base} {...props}>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				fill="currentColor"
				d="M24.4999 0C27.7173 0 30.9032 0.633683 33.8757 1.86491C36.8482 3.09614 39.549 4.90079 41.8241 7.17583C44.0991 9.45086 45.9038 12.1517 47.135 15.1242C48.3663 18.0967 49 21.2826 49 24.4999C49 38.0309 38.0309 49 24.4999 49C10.9691 49 0 38.0309 0 24.4999C0 10.9691 10.9691 0 24.4999 0ZM26.95 26.95H22.05C15.9845 26.95 10.7772 30.6235 8.53116 35.8674C12.0849 40.8506 17.913 44.1 24.4999 44.1C31.0869 44.1 36.915 40.8506 40.4688 35.8671C38.2228 30.6235 33.0155 26.95 26.95 26.95ZM24.4999 7.34999C20.4407 7.34999 17.1499 10.6407 17.1499 14.7C17.1499 18.7593 20.4407 22.05 24.4999 22.05C28.5592 22.05 31.8499 18.7593 31.8499 14.7C31.8499 10.6407 28.5593 7.34999 24.4999 7.34999Z"
			/>
		</svg>
	);
}

/** Hamburger for the small-screen top bar (not in Figma; daisyUI drawer toggle). */
export function MenuIcon(props: IconProps) {
	return (
		<svg viewBox="0 0 24 24" {...base} {...props}>
			<path
				stroke="currentColor"
				strokeWidth="2"
				strokeLinecap="round"
				d="M4 7h16M4 12h16M4 17h16"
			/>
		</svg>
	);
}

/*
 * Icons below are not in the Figma export (those modules were drawn later).
 * They follow the same language as the tabler glyphs the design uses: 24×24
 * box, 2px round stroke, no fill — so they sit on the pill indistinguishably.
 */
function Stroke({ d, ...props }: IconProps & { d: string }) {
	return (
		<svg viewBox="0 0 24 24" {...base} {...props}>
			<path d={d} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

/** tabler:id — accreditation. */
export function IdCardIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M3 6a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6zM7 10a2 2 0 1 0 4 0 2 2 0 1 0-4 0zM15 8h2M15 12h2M7 16h10"
		/>
	);
}

/** tabler:bed — accommodation. */
export function BedIcon(props: IconProps) {
	return <Stroke {...props} d="M3 7v11M3 14h18M21 18v-8a2 2 0 0 0-2-2h-8v6M7 10a1 1 0 1 0 2 0 1 1 0 1 0-2 0" />;
}

/** tabler:lifebuoy — incident helpdesk. */
export function LifebuoyIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M12 12m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0-18 0M15 15l3.35 3.35M9 15l-3.35 3.35M5.65 5.65 9 9M18.35 5.65 15 9"
		/>
	);
}

/** tabler:package — inventory. */
export function PackageIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3M12 12l8-4.5M12 12v9M12 12L4 7.5M16 5.25L8 9.75"
		/>
	);
}

/** tabler:building-warehouse — WSL inventory. */
export function WarehouseIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M3 21V11l9-6 9 6v10M7 21v-8h10v8M9 21v-4M13 21v-4M17 21v-4M12 21v-8"
		/>
	);
}

/** tabler:report-analytics — central report engine. */
export function ReportIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v0a2 2 0 0 1-2 2h-2a2 2 0 0 1-2-2M9 17v-4M12 17v-1M15 17v-2"
		/>
	);
}

/** tabler:settings — game settings. */
export function SettingsIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 0 0-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 0 0-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 0 0-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 0 0-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 0 0 1.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM9 12a3 3 0 1 0 6 0 3 3 0 1 0-6 0"
		/>
	);
}

/** tabler:users — users. */
export function UsersIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0-8 0M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2M16 3.13a4 4 0 0 1 0 7.75M21 21v-2a4 4 0 0 0-3-3.85"
		/>
	);
}

/** tabler:user-circle — my profile (small variant of the avatar glyph). */
export function UserIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0-18 0M12 10m-3 0a3 3 0 1 0 6 0a3 3 0 1 0-6 0M6.168 18.849A4 4 0 0 1 10 16h4a4 4 0 0 1 3.834 2.855"
		/>
	);
}

/** tabler:switch-horizontal — switch role, in the account menu. */
export function SwitchIcon(props: IconProps) {
	return <Stroke {...props} d="M16 3l4 4-4 4M20 7H4M8 13l-4 4 4 4M4 17h16" />;
}

/** tabler:key — change password, in the account menu. */
export function KeyIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M16.555 3.843l3.602 3.602a2.877 2.877 0 0 1 0 4.069l-2.643 2.643a2.877 2.877 0 0 1-4.069 0l-.301-.301-6.558 6.558a2 2 0 0 1-1.239.578L5.172 21H3v-2.172a2 2 0 0 1 .586-1.414l6.558-6.558-.301-.301a2.877 2.877 0 0 1 0-4.069l2.643-2.643a2.877 2.877 0 0 1 4.069 0zM15 9h.01"
		/>
	);
}

/** tabler:logout — sign out, in the account menu. */
export function LogoutIcon(props: IconProps) {
	return (
		<Stroke
			{...props}
			d="M14 8V6a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h5a2 2 0 0 0 2-2v-2M9 12h12l-3-3M18 15l3-3"
		/>
	);
}

/*
 * Glyphs from the designer's GMS prototype (SEAGAMES-UI admin.js / l3.js):
 * one 24px grid, 1.8 stroke, round caps — copied path for path so the
 * sidebar matches her frames.
 */
function Line({ children, ...props }: IconProps & { children: ReactNode }) {
	return (
		<svg
			viewBox="0 0 24 24"
			{...base}
			stroke="currentColor"
			strokeWidth="1.8"
			strokeLinecap="round"
			strokeLinejoin="round"
			{...props}
		>
			{children}
		</svg>
	);
}

/** Dashboard. */
export function HomeIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M4 11 12 4.5 20 11" />
			<path d="M6 9.5v10h12v-10" />
			<path d="M10 19.5v-5h4v5" />
		</Line>
	);
}

/** Register New Record. */
export function UserPlusIcon(props: IconProps) {
	return (
		<Line {...props}>
			<circle cx="10" cy="8.5" r="3.6" />
			<path d="M3.5 20c.8-3.6 3.4-5.6 6.5-5.6 1.4 0 2.7.4 3.8 1.2" />
			<path d="M18 13.5v6M15 16.5h6" />
		</Line>
	);
}

/** View Records. */
export function ListIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M9 7h11M9 12h11M9 17h11M4.5 7h.01M4.5 12h.01M4.5 17h.01" />
		</Line>
	);
}

/** Shortlist. */
export function ClipboardCheckIcon(props: IconProps) {
	return (
		<Line {...props}>
			<rect x="5.5" y="4.5" width="13" height="16" rx="2" />
			<path d="M9 4.5v-1h6v1" />
			<path d="m9 13 2 2 4-4" />
		</Line>
	);
}

/** E-Accreditation (and the accreditation group). */
export function CardIcon(props: IconProps) {
	return (
		<Line {...props}>
			<rect x="3" y="5.5" width="18" height="13" rx="2.2" />
			<circle cx="8.5" cy="11" r="2" />
			<path d="M6 15.5c.5-1.3 1.4-2 2.5-2s2 .7 2.5 2M13.5 10h4.5M13.5 13h3" />
		</Line>
	);
}

/** Import File. */
export function SheetIcon(props: IconProps) {
	return (
		<Line {...props}>
			<rect x="4.5" y="3.5" width="15" height="17" rx="2" />
			<path d="M4.5 9h15M4.5 14.5h15M10 9v11.5" />
		</Line>
	);
}

/** Arrival & Departure. */
export function PlaneLandingIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M3 20.5h18" />
			<path d="M4.6 10.4 3 8.8l1.8-4 1 .5c.5.25.8.75.8 1.3s.3 1.05.8 1.3l1 .5 2.7-5.4.95.48a1.8 1.8 0 0 1 1 1.37l.65 4.86a1.8 1.8 0 0 0 .98 1.37l3.96 1.98c.38.2.7.5.9.86l.54.93c.44.8-.05 1.78-.95 1.89l-1.06.14a2.2 2.2 0 0 1-1.23-.22L5.07 10.74a1.8 1.8 0 0 1-.47-.34Z" />
		</Line>
	);
}

/** Transportation. */
export function BusIcon(props: IconProps) {
	return (
		<Line {...props}>
			<rect x="5" y="3.5" width="14" height="14" rx="2.5" />
			<path d="M5 11h14M8 17.5V20M16 17.5V20" />
			<circle cx="8.5" cy="14.3" r=".8" />
			<circle cx="15.5" cy="14.3" r=".8" />
		</Line>
	);
}

/** Accommodation, in the prototype's line. */
export function BedLineIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M3.5 18.5V6M3.5 14h17v4.5M20.5 14v-2.5a3 3 0 0 0-3-3H11V14" />
			<circle cx="7.3" cy="11" r="1.8" />
		</Line>
	);
}

/** Food & Beverages. */
export function MealIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M7 3.5v17M4.5 3.5V8A2.5 2.5 0 0 0 9.5 8V3.5" />
			<path d="M16.5 20.5V3.5c-2 1-3 3.5-3 6.5v3h3" />
		</Line>
	);
}

/** Merchandising: a shopping bag, in the same 1.8 line as the prototype's glyphs. */
export function ShoppingBagIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M5.5 7.5h13l-1 13h-11l-1-13z" />
			<path d="M9 10V6.5a3 3 0 0 1 6 0V10" />
		</Line>
	);
}

/** Section / group disclosure; the caller rotates it −90° when closed. */
export function ChevronDownIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="m6 9 6 6 6-6" />
		</Line>
	);
}

/** Header minimise ("<"); the caller flips it to ">" on the rail. */
export function ChevronBackIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M15 5l-7 7 7 7" />
		</Line>
	);
}

/** User card disclosure. */
export function ChevronForwardIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="m9 6 6 6-6 6" />
		</Line>
	);
}

/** Closes the small-screen drawer. */
export function CloseIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M6 6l12 12M18 6 6 18" />
		</Line>
	);
}

/*
 * Page glyphs for the rows under a group (2026-10-23: every page row has its
 * icon). Same 1.8 line as the prototype's; geometry from Lucide (ISC),
 * copied path for path — the GMS takes no icon dependency.
 */

/** Expression of Interest: what a NOC aims at. */
export function TargetIcon(props: IconProps) {
	return (
		<Line {...props}>
			<circle cx="12" cy="12" r="9" />
			<circle cx="12" cy="12" r="5.5" />
			<circle cx="12" cy="12" r="2" />
		</Line>
	);
}

/** Entry by Number. */
export function HashIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
		</Line>
	);
}

/** AC Card Printing. */
export function PrinterIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
			<path d="M6 9V3a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v6" />
			<rect x="6" y="14" width="12" height="8" rx="1" />
		</Line>
	);
}

/** A dashboard page under a group (Contingent, Volunteers). */
export function LayoutDashboardIcon(props: IconProps) {
	return (
		<Line {...props}>
			<rect x="3" y="3" width="7" height="9" rx="1" />
			<rect x="14" y="3" width="7" height="5" rx="1" />
			<rect x="14" y="12" width="7" height="9" rx="1" />
			<rect x="3" y="16" width="7" height="5" rx="1" />
		</Line>
	);
}

/** Responsible Organizations. */
export function BuildingIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
			<path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
			<path d="M10 6h4M10 10h4M10 14h4M10 18h4" />
		</Line>
	);
}

/** Sub Agencies (national federations under an organisation). */
export function NetworkIcon(props: IconProps) {
	return (
		<Line {...props}>
			<rect x="16" y="16" width="6" height="6" rx="1" />
			<rect x="2" y="16" width="6" height="6" rx="1" />
			<rect x="9" y="2" width="6" height="6" rx="1" />
			<path d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3M12 12V8" />
		</Line>
	);
}

/** Affiliated Organizations. */
export function LinkIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
			<path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
		</Line>
	);
}

/** Main / Sub Functions (the categories). */
export function ShapesIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z" />
			<rect x="3" y="14" width="7" height="7" rx="1" />
			<circle cx="17.5" cy="17.5" r="3.5" />
		</Line>
	);
}

/** Zone Access. */
export function ShieldCheckIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" />
			<path d="m9 12 2 2 4-4" />
		</Line>
	);
}

/** Card Handover Locations. */
export function HandIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M18 11V6a2 2 0 0 0-4 0M14 10V4a2 2 0 0 0-4 0v2M10 10.5V6a2 2 0 0 0-4 0v8" />
			<path d="M18 8a2 2 0 1 1 4 0v6a8 8 0 0 1-8 8h-2c-2.8 0-4.5-.86-5.99-2.34l-3.6-3.6a2 2 0 0 1 2.83-2.82L7 15" />
		</Line>
	);
}

/** Entry Points (airports, land crossings). */
export function DoorOpenIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M11 20H2M22 20h-3M14 12h.01" />
			<path d="M11 4.562v16.157a1 1 0 0 0 1.242.97L19 20V5.562a2 2 0 0 0-1.515-1.94l-4-1A2 2 0 0 0 11 4.561z" />
			<path d="M11 4H8a2 2 0 0 0-2 2v14" />
		</Line>
	);
}

/** Quotas. */
export function GaugeIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="m12 14 4-4" />
			<path d="M3.34 19a10 10 0 1 1 17.32 0" />
		</Line>
	);
}

/** Entry Windows. */
export function CalendarClockIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M21 7.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3.5M16 2v4M8 2v4M3 10h5" />
			<path d="M17.5 17.5 16 16.3V14" />
			<circle cx="16" cy="16" r="6" />
		</Line>
	);
}

/** Sector Management: the numbered sectors. */
export function ListOrderedIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M10 6h11M10 12h11M10 18h11M4 10h2M4 6h1v4M6 18H4c0-1 2-2 2-3s-1-1.5-2-1" />
		</Line>
	);
}

/** Sports. */
export function TrophyIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6M18 9h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16" />
			<path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
			<path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
		</Line>
	);
}

/** Venues. */
export function MapPinIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />
			<circle cx="12" cy="10" r="3" />
		</Line>
	);
}

/** Modules (Users). */
export function LayersIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M12.83 2.18a2 2 0 0 0-1.66 0L2.6 6.08a1 1 0 0 0 0 1.83l8.58 3.91a2 2 0 0 0 1.66 0l8.58-3.9a1 1 0 0 0 0-1.83Z" />
			<path d="m22 17.65-9.17 4.16a2 2 0 0 1-1.66 0L2 17.65M22 12.65l-9.17 4.16a2 2 0 0 1-1.66 0L2 12.65" />
		</Line>
	);
}

/** User Groups: who acts where, with what. */
export function UserCogIcon(props: IconProps) {
	return (
		<Line {...props}>
			<circle cx="18" cy="15" r="3" />
			<circle cx="9" cy="7" r="4" />
			<path d="M10 15H6a4 4 0 0 0-4 4v2" />
			<path d="m21.7 16.4-.9-.3M15.2 13.9l-.9-.3M16.6 18.7l.3-.9M19.1 12.2l.3-.9M19.6 18.7l-.4-1M16.8 12.3l-.4-1M14.3 16.6l1-.4M20.7 13.8l1-.4" />
		</Line>
	);
}

/** Change Logs: a page with a clock — what changed, and when. */
export function HistoryIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M3.5 12a8.5 8.5 0 1 0 2.5-6" />
			<path d="M3.5 4.5V8h3.5" />
			<path d="M12 7.5V12l3 2" />
		</Line>
	);
}

/** A medal on its ribbon — the Contingent Dashboard's Medals. */
export function MedalIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M7.21 15 2.66 7.14a2 2 0 0 1 .13-2.2L4.4 2.8A2 2 0 0 1 6 2h12a2 2 0 0 1 1.6.8l1.6 2.14a2 2 0 0 1 .14 2.2L16.79 15" />
			<path d="M11 12 5.12 2.2M13 12l5.88-9.8M8 7h8" />
			<circle cx="12" cy="17" r="5" />
			<path d="M12 18v-2h-.5" />
		</Line>
	);
}

/** A record broken — the Contingent Dashboard's Records. */
export function ZapIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z" />
		</Line>
	);
}

/** "vs" — a head-to-head opponent. */
export function SwordsIcon(props: IconProps) {
	return (
		<Line {...props}>
			<path d="M14.5 17.5 3 6V3h3l11.5 11.5M13 19l6-6M16 16l4 4M19 21l2-2M14.5 6.5 18 3h3v3l-3.5 3.5M5 14l4 4M7 17l-3 3M3 19l2 2" />
		</Line>
	);
}

/** A time of day. */
export function ClockIcon(props: IconProps) {
	return (
		<Line {...props}>
			<circle cx="12" cy="12" r="10" />
			<path d="M12 6v6l4 2" />
		</Line>
	);
}
